import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCpu, FiLayout, FiSearch, FiZap, FiTarget, FiShield, FiTrendingUp, FiMousePointer } = FiIcons;

const SEOSection = () => {
  const capabilities = [
    {
      title: "Advanced Architecture",
      desc: "Architecting scalable full-stack applications with React, Node.js, and modern cloud infrastructure.",
      icon: FiCpu,
      color: "text-indigo-600",
      bg: "bg-indigo-50"
    },
    {
      title: "Psychology-Driven UX",
      desc: "Interfaces designed using cognitive behavioral patterns to maximize retention and conversion rates.",
      icon: FiMousePointer,
      color: "text-emerald-600",
      bg: "bg-emerald-50"
    },
    {
      title: "Growth Engineering",
      desc: "Deep technical SEO integration and performance tuning for competitive organic ranking.",
      icon: FiTrendingUp,
      color: "text-purple-600",
      bg: "bg-purple-50"
    },
    {
      title: "Ironclad Security",
      desc: "Enterprise-grade security protocols ensuring data integrity and user protection by default.",
      icon: FiShield,
      color: "text-rose-600",
      bg: "bg-rose-50"
    }
  ];

  return (
    <section className="w-full max-w-6xl mx-auto mt-40 px-6 pb-40">
      <div className="text-center mb-24">
        <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-8 tracking-tighter">
          Engineered for <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-emerald-500">Impact</span>
        </h2>
        <p className="text-slate-500 max-w-3xl mx-auto text-xl leading-relaxed font-medium">
          Shawn Ryder Digital isn't just another agency. We are technical partners dedicated to 
          transforming complex business logic into seamless, high-converting digital products.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-32">
        {capabilities.map((cap, i) => (
          <div key={i} className="bg-white border border-slate-200 shadow-lg shadow-slate-200/50 p-10 rounded-[2rem] hover:border-indigo-300 hover:shadow-xl transition-all duration-500 group relative overflow-hidden">
            <div className={`w-14 h-14 ${cap.bg} rounded-2xl flex items-center justify-center mb-8 border border-white/50 group-hover:scale-110 transition-transform shadow-sm`}>
              <SafeIcon icon={cap.icon} className={`text-3xl ${cap.color}`} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-4 tracking-tight">{cap.title}</h3>
            <p className="text-slate-500 text-sm leading-relaxed font-medium">{cap.desc}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-[3rem] p-10 md:p-20 border border-slate-200 shadow-2xl shadow-slate-200/50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-indigo-50 to-transparent pointer-events-none" />
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative z-10">
          <div className="lg:col-span-7">
            <h3 className="text-3xl md:text-5xl font-black text-slate-900 mb-10 tracking-tighter">The Velocity Workflow</h3>
            <div className="space-y-10">
              {[
                { step: "01", t: "Strategic Discovery", d: "Defining KPIs, audience segments, and technical constraints." },
                { step: "02", t: "Rapid Prototyping", d: "High-fidelity UX blueprints that validate logic before code." },
                { step: "03", t: "Modular Development", d: "Clean, documented, and strictly typed codebases built for scale." },
                { step: "04", t: "Optimization Loop", d: "Post-launch analytics and performance tuning for maximum ROI." }
              ].map((item, idx) => (
                <div key={idx} className="flex gap-6 group">
                  <span className="text-indigo-200 font-black text-2xl group-hover:text-indigo-500 transition-colors">{item.step}</span>
                  <div>
                    <h4 className="text-slate-900 text-xl font-bold mb-2 tracking-tight">{item.t}</h4>
                    <p className="text-slate-500 text-base font-medium">{item.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-200 shadow-inner">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold shadow-md shadow-indigo-600/20">SR</div>
                <div>
                  <div className="text-slate-900 font-bold">Shawn Ryder</div>
                  <div className="text-indigo-600 text-xs font-bold uppercase tracking-widest">Lead Engineer</div>
                </div>
              </div>
              <p className="text-slate-600 italic text-lg leading-relaxed font-medium">
                "I don't just build websites; I build business assets. Every line of code is written with your bottom line in mind."
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm text-center">
                <div className="text-3xl font-black text-emerald-500 mb-1">100%</div>
                <div className="text-slate-400 text-[10px] uppercase font-bold tracking-widest">Commitment</div>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm text-center">
                <div className="text-3xl font-black text-indigo-500 mb-1">24h</div>
                <div className="text-slate-400 text-[10px] uppercase font-bold tracking-widest">Response</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SEOSection;